import { useEffect, useState } from "react";

import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Sheet } from "@/components/ui/Sheet";
import { ApiError, apiFetch } from "@/lib/api";
import type { DocumentListItemResource, DocumentReplacementResource } from "@/lib/api-types";
import { labelFor } from "@/lib/field-labels";
import { useIsPhone } from "@/lib/use-media-query";

/**
 * "Replace" (Story 15.2, AC) — a real upload form, not a bare confirm,
 * mirroring `ReplaceEquipmentDialog.tsx`'s own shape closely (Dialog on
 * desktop, Sheet on phone — the identical `useIsPhone` toggle). Deliberately
 * narrower than that dialog: a document has only two things an Owner ever
 * enters here — the new file itself, and (spec judgement call 2) an
 * optional expiration date, pre-filled from the OLD document's own value as
 * an editable suggestion, never silently carried over unedited. Every other
 * field this story's own AC names — `entity_type`/`entity_id`/`category` —
 * is never asked here at all: it always carries over from the old row,
 * resolved entirely server-side (`app/api/documents.py`'s own `POST
 * /documents/{id}/replace`, which does not even declare headers for them).
 *
 * Uploads via the same raw-bytes-as-body + header-carried-metadata
 * convention `AddDocumentDialog.tsx` already uses for a plain upload
 * (`apiFetch`'s own body accepts a `File` directly, since `File` IS a
 * `Blob`) — never `multipart/form-data`.
 */
export function ReplaceDocumentDialog({
  open,
  onOpenChange,
  oldDocument,
  onReplaced,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  oldDocument: DocumentListItemResource;
  onReplaced: (result: DocumentReplacementResource) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [expirationDate, setExpirationDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const isPhone = useIsPhone();
  const Container = isPhone ? Sheet : Dialog;

  useEffect(() => {
    if (open) {
      // Pre-filled as an editable suggestion (spec judgement call 2) —
      // never silently copied unedited: the Owner sees the old value here
      // and can change or clear it before submitting.
      setExpirationDate(oldDocument.expiration_date ?? "");
    } else {
      setFile(null);
      setExpirationDate("");
      setError(undefined);
    }
    // Only re-runs when the dialog opens (or targets a different old
    // document) — never once the Owner has started editing the field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, oldDocument.id]);

  const canSubmit = file !== null && !isSubmitting;

  async function handleSubmit() {
    if (file === null || !canSubmit) {
      return;
    }
    setIsSubmitting(true);
    setError(undefined);
    try {
      const headers: Record<string, string> = {
        // Percent-encoded, mirroring `AddDocumentDialog.tsx`'s own identical
        // Fix 2 discipline (a raw non-Latin-1 filename would otherwise throw
        // inside the browser's own `Headers` constructor before this
        // request is even sent).
        "X-PropDesk-Filename": encodeURIComponent(file.name),
      };
      if (expirationDate !== "") {
        headers["X-PropDesk-Expiration-Date"] = expirationDate;
      }
      const result = await apiFetch<DocumentReplacementResource>(`/documents/${oldDocument.id}/replace`, {
        method: "POST",
        headers,
        body: file,
      });
      onOpenChange(false);
      onReplaced(result);
    } catch (submitError) {
      setError(
        submitError instanceof ApiError ? submitError.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Container
      open={open}
      onOpenChange={onOpenChange}
      title="Replace document"
      description={`${oldDocument.original_filename} will be marked superseded, with a real link to the new file you upload here.`}
    >
      <form
        className="mt-4 flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">File</span>
          <Input
            type="file"
            autoFocus
            accept=".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">
            {labelFor("expiration_date")} (optional)
          </span>
          <Input
            type="date"
            value={expirationDate}
            onChange={(event) => setExpirationDate(event.target.value)}
          />
        </label>

        {error ? (
          <p role="alert" className="text-meta text-danger">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {isSubmitting ? "Replacing…" : "Replace document"}
          </Button>
        </div>
      </form>
    </Container>
  );
}
